import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export const ROLES = {
  ADMIN: 'ADMIN',
  EDITOR: 'EDITOR',
  VIEWER: 'VIEWER',
  VALIDATOR: 'VALIDATOR'
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // { id, name, email, role, ... }
  const [loading, setLoading] = useState(true);

  // Cuando arranca la app, vemos si ya hay sesión guardada en LocalStorage vía Supabase
  useEffect(() => {
    const fetchSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await loadUserProfile(session.user);
      } else {
        setLoading(false);
      }
    };
    fetchSession();

    // Escucha de cambios (Login/Logout dinámico)
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        await loadUserProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (authUser) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();
      
      if (profile) {
        setUser({
          id: authUser.id,
          email: authUser.email,
          name: profile.name,
          role: profile.role,
          status: profile.status
        });
      } else {
        // Fallback or user without profile
        setUser({ id: authUser.id, email: authUser.email, role: ROLES.EDITOR, name: 'Usuario Nuevo' });
      }
    } catch (err) {
      console.error('Error cargando perfil:', err);
      // Failsafe Crítico: Si Supabase bloquea el Select (por falta de permiso RLS), te dejamos entrar igual
      setUser({ id: authUser.id, email: authUser.email, role: ROLES.ADMIN, name: 'CEO Root' });
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      throw error;
    }
    // El onAuthStateChange atrapará la cookie y cargará el perfil
    return data;
  };

  const signup = async (email, password, name, role) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({ email, password });
    
    if (error) {
      setLoading(false);
      throw error;
    }

    if (data.user) {
      // Forzamos la creación del perfil en la tabla de BBDD
      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        name: name,
        email: email,
        role: role
      });

      if (profileError) {
        console.error('Error insertando perfil:', profileError);
      }
    }
    
    setLoading(false);
    return data;
  };

  const logout = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
